import { useState, useRef, useEffect, useMemo } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import flags from "react-phone-number-input/flags";
import enLabels from "react-phone-number-input/locale/en.json";
import { cn } from "@/lib/utils";

/**
 * Searchable country picker (flag + name + dial code) built on the same data set
 * react-phone-number-input uses, so a chosen country's ISO-2 code lines up with
 * libphonenumber-js phone validation elsewhere in the form.
 *
 * `value` / `onChange` deal in ISO-2 codes (e.g. "BD", "US"). Bangladesh is pinned
 * to the top of the list since it's the primary market.
 */
export default function CountrySelect({ value = "", onChange, error = false, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const countries = useMemo(() => {
    const list = getCountries()
      .map((code) => ({
        code,
        name: enLabels[code] || code,
        dial: getCountryCallingCode(code),
        Flag: flags[code],
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    // Pin Bangladesh first.
    const bdIdx = list.findIndex((c) => c.code === "BD");
    if (bdIdx > 0) {
      const [bd] = list.splice(bdIdx, 1);
      list.unshift(bd);
    }
    return list;
  }, []);

  const selected = countries.find((c) => c.code === value) || null;

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 0);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? countries.filter((c) => c.name.toLowerCase().includes(q) || `+${c.dial}`.includes(q) || c.code.toLowerCase() === q)
    : countries;

  const handleSelect = (code) => {
    onChange(code);
    setOpen(false);
    setQuery("");
  };

  const triggerCls =
    "w-full px-3 py-2 border rounded-md text-sm flex items-center gap-2 " +
    "focus:outline-none focus:ring-2 focus:ring-orange-400 " +
    "bg-white dark:bg-zinc-700 dark:text-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed " +
    (error ? "border-red-500 focus:ring-red-400" : "border-zinc-300 dark:border-zinc-600");

  return (
    <div ref={containerRef} className="relative">
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)} className={triggerCls}>
        {selected ? (
          <>
            <span className="w-5 h-4 flex-shrink-0 overflow-hidden rounded-[2px]">
              {selected.Flag ? <selected.Flag title={selected.name} /> : null}
            </span>
            <span className="truncate">{selected.name}</span>
            <span className="text-zinc-400">+{selected.dial}</span>
          </>
        ) : (
          <span className="text-zinc-400">Select country</span>
        )}
        <IconChevronDown
          size={16}
          className={cn("ml-auto text-zinc-400 transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="absolute z-[300] left-0 right-0 mt-1 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg">
          <div className="p-2 border-b border-zinc-100 dark:border-zinc-800">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country…"
              className="w-full px-2.5 py-1.5 border border-zinc-200 dark:border-zinc-700 rounded-md text-sm bg-white dark:bg-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-orange-400"
              autoComplete="off"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto text-sm">
            {filtered.map((c) => {
              const Flag = c.Flag;
              return (
                <li
                  key={c.code}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(c.code);
                  }}
                  className={cn(
                    "px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-800 dark:text-zinc-100",
                    c.code === value && "bg-orange-50 dark:bg-orange-950/30"
                  )}
                >
                  <span className="w-5 h-4 flex-shrink-0 overflow-hidden rounded-[2px]">
                    {Flag ? <Flag title={c.name} /> : null}
                  </span>
                  <span className="truncate flex-1">{c.name}</span>
                  <span className="text-zinc-400">+{c.dial}</span>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-center text-zinc-400">No country found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
